import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../../supabaseClient";

// ── Types ─────────────────────────────────────────────────────

interface RsvpEntry {
  id: number;
  created_at: string;
  name: string;
  attend: string;
  message: string;
  with_guest: boolean;
  guest_name: string | null;
}

// ── Styled ────────────────────────────────────────────────────

const PageWrapper = styled(Box)({
  backgroundColor: "#f5f5f5",
  minHeight: "100vh",
  padding: "40px 24px",
});

const Title = styled(Typography)({
  fontSize: "1.4rem",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#333",
  marginBottom: "8px",
});

const StatsRow = styled(Box)({
  display: "flex",
  gap: "16px",
  marginBottom: "32px",
  flexWrap: "wrap",
});

const StatCard = styled(Box)<{ color?: string }>(({ color }) => ({
  backgroundColor: color || "#e53935",
  color: "white",
  padding: "16px 24px",
  minWidth: "120px",
  textAlign: "center",
}));

const StatNumber = styled(Typography)({
  fontSize: "1.8rem",
  fontWeight: "bold",
  lineHeight: 1,
});

const StatLabel = styled(Typography)({
  fontSize: "0.7rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  opacity: 0.9,
  marginTop: "4px",
});

const Table = styled(Box)({
  width: "100%",
  backgroundColor: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  overflow: "auto",
});

const TableHead = styled(Box)({
  display: "grid",
  gridTemplateColumns: "2fr 2fr 1.4fr 2fr 3fr",
  backgroundColor: "#333",
  color: "white",
  padding: "12px 16px",
  gap: "8px",
});

const TableRow = styled(Box)<{ even?: string }>(({ even }) => ({
  display: "grid",
  gridTemplateColumns: "2fr 2fr 1.4fr 2fr 3fr",
  padding: "12px 16px",
  gap: "8px",
  backgroundColor: even === "true" ? "#fafafa" : "#fff",
  borderBottom: "1px solid #eee",
  alignItems: "center",
  "&:hover": { backgroundColor: "#fff3f3" },
}));

const ColHeader = styled(Typography)({
  fontSize: "0.68rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  fontWeight: "bold",
});

const Cell = styled(Typography)({
  fontSize: "0.78rem",
  color: "#555",
});

const attendLabel: Record<string, { label: string; color: string }> = {
  les_deux:  { label: "Cérémonie & Réception", color: "#4caf50" },
  ceremonie: { label: "Cérémonie",             color: "#2196f3" },
  reception: { label: "Réception",             color: "#ff9800" },
  absent:    { label: "Absent",                color: "#9e9e9e" },
};

// ── Composant ─────────────────────────────────────────────────

export default function AdminRsvp() {
  const [session, setSession] = useState<Session | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Formulaire de connexion
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Données RSVP
  const [data, setData] = useState<RsvpEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Suivi de la session Supabase
  useEffect(() => {
    supabase.auth.getSession().then(({ data: d }) => {
      setSession(d.session);
      setAuthChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) =>
      setSession(s)
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  // Chargement des réponses UNIQUEMENT une fois connecté
  useEffect(() => {
    if (!session) return;
    const fetchRsvp = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase
        .from("rsvp")
        .select("*")
        .order("created_at", { ascending: false });
      if (!error && rows) setData(rows);
      setLoading(false);
    };
    fetchRsvp();
  }, [session]);

  const handleLogin = async () => {
    setAuthLoading(true);
    setAuthError("");
    const email = user.trim().toLowerCase();
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // On affiche la VRAIE raison (utile pour diagnostiquer) :
        // "Invalid login credentials" = mauvais identifiant/mot de passe
        // "Email not confirmed"       = compte créé sans "Auto Confirm"
        // "Email logins are disabled" = provider Email désactivé dans Supabase
        console.error("Connexion admin échouée :", error);
        setAuthError(`Échec : ${error.message} (compte essayé : ${email})`);
      }
    } catch (e) {
      // Ici la requête n'a même pas pu partir (config Supabase / réseau)
      console.error("Erreur de connexion au serveur :", e);
      setAuthError(
        "Impossible de joindre le serveur. Vérifie les variables REACT_APP_SUPABASE_URL / _ANON_KEY du déploiement."
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setData([]);
  };

  // ── Vérification de session en cours ──
  if (!authChecked) {
    return (
      <PageWrapper sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#e53935" }} />
      </PageWrapper>
    );
  }

  // ── Écran de connexion (non authentifié) ──
  if (!session) {
    return (
      <PageWrapper sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            padding: "40px",
            maxWidth: "360px",
            width: "100%",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "#e53935",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              fontSize: "0.85rem",
              margin: "0 auto 16px",
            }}
          >
            W&amp;D
          </Box>
          <Title sx={{ fontSize: "1.05rem", mb: 3 }}>Espace administrateur</Title>

          {authError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "6px", textAlign: "left" }}>
              {authError}
            </Alert>
          )}

          <TextField
            label="E-mail"
            type="email"
            placeholder="admin@winnie-donald.com"
            autoComplete="username"
            fullWidth
            size="small"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Mot de passe"
            type="password"
            fullWidth
            size="small"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogin();
            }}
            sx={{ mb: 3 }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={authLoading}
            sx={{
              backgroundColor: "#e53935",
              "&:hover": { backgroundColor: "#c62828" },
              borderRadius: "6px",
              py: 1.2,
              letterSpacing: "0.1em",
            }}
          >
            {authLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Se connecter"
            )}
          </Button>
        </Box>
      </PageWrapper>
    );
  }

  // ── Tableau de bord (authentifié) ──
  const total       = data.length;
  const presents    = data.filter((r) => r.attend !== "absent").length;
  const absents     = data.filter((r) => r.attend === "absent").length;
  const avecGuest   = data.filter((r) => r.with_guest).length;
  const totalPersons = presents + avecGuest; // mariés non compris

  return (
    <PageWrapper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Title>Administration — Réponses RSVP</Title>
          <Typography sx={{ color: "#aaa", fontSize: "0.8rem", mb: 3 }}>
            Mariage de Winnie &amp; Donald — 28 &amp; 29 Août 2026
          </Typography>
        </Box>
        <Button
          onClick={handleLogout}
          sx={{
            color: "#555",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
          }}
        >
          Déconnexion
        </Button>
      </Box>

      {/* Statistiques */}
      <StatsRow>
        <StatCard color="#333">
          <StatNumber>{total}</StatNumber>
          <StatLabel>Réponses</StatLabel>
        </StatCard>
        <StatCard color="#4caf50">
          <StatNumber>{presents}</StatNumber>
          <StatLabel>Présents</StatLabel>
        </StatCard>
        <StatCard color="#9e9e9e">
          <StatNumber>{absents}</StatNumber>
          <StatLabel>Absents</StatLabel>
        </StatCard>
        <StatCard color="#ff9800">
          <StatNumber>{avecGuest}</StatNumber>
          <StatLabel>Avec invité</StatLabel>
        </StatCard>
        <StatCard color="#e53935">
          <StatNumber>{totalPersons}</StatNumber>
          <StatLabel>Total personnes</StatLabel>
        </StatCard>
      </StatsRow>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress sx={{ color: "#e53935" }} />
        </Box>
      ) : (
        <Table>
          {/* En-tête */}
          <TableHead>
            <ColHeader>Nom</ColHeader>
            <ColHeader>Présence</ColHeader>
            <ColHeader>Accompagné(e)</ColHeader>
            <ColHeader>Invité / Menu</ColHeader>
            <ColHeader>Message</ColHeader>
          </TableHead>

          {/* Lignes */}
          {data.length === 0 ? (
            <Box sx={{ padding: "32px", textAlign: "center", color: "#aaa", fontSize: "0.85rem" }}>
              Aucune réponse pour le moment.
            </Box>
          ) : (
            data.map((row, i) => {
              const attend = attendLabel[row.attend] || { label: row.attend, color: "#333" };
              return (
                <TableRow key={row.id} even={(i % 2 === 0).toString()}>
                  <Cell>{row.name}</Cell>
                  <Box>
                    <Chip
                      label={attend.label}
                      size="small"
                      sx={{
                        backgroundColor: attend.color,
                        color: "white",
                        fontSize: "0.65rem",
                        height: "22px",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                  <Cell>{row.with_guest ? "✅ Oui" : "Non"}</Cell>
                  <Box>
                    {row.with_guest && row.guest_name ? (
                      <Cell sx={{ fontWeight: "bold" }}>{row.guest_name}</Cell>
                    ) : (
                      <Cell sx={{ color: "#ccc" }}>—</Cell>
                    )}
                  </Box>
                  <Cell sx={{ fontSize: "0.72rem", fontStyle: "italic", color: "#aaa" }}>
                    {row.message || "—"}
                  </Cell>
                </TableRow>
              );
            })
          )}
        </Table>
      )}
    </PageWrapper>
  );
}
